import lodash from "lodash"
import {
    UsersActivateInput,
    UsersDeactivateInput,
    UsersUpdateAdminInput,
    UsersUpdateOrganizationMembershipInput,
} from "../../generated/graphql"
import { UpdateUserFormI, UpdateUserModalMutationInputs } from "./types"
import { isFormValid } from "./validation"
import { Sanitize } from "../../util/sanitize/sanitize"

const sanitizeForm = (form: UpdateUserFormI): UpdateUserFormI => {
    return {
        ...form,
        firstName: Sanitize.string(form.firstName),
        lastName: Sanitize.string(form.lastName),
    }
}

/**
 * Helper function to disable the submit button on the update user modal.
 * Used to prevent the user from submitting the form if the form is invalid.
 *
 * The form should be disabled if:
 * - There is an API error
 * - The api is in a loading state
 * - The form is equal to the initial form
 * - The form is invalid
 */
const isSubmitFormDisabled = (
    initialForm: UpdateUserFormI,
    updatedForm: UpdateUserFormI,
    apiError: boolean,
    apiLoading: boolean
) => {
    if (apiError || apiLoading) {
        return true
    }

    if (lodash.isEqual(initialForm, updatedForm)) {
        return true
    }

    if (!isFormValid(updatedForm)) {
        return true
    }

    return false
}

/**
 * Helper function to get the mutation inputs for the update user modal.
 * There are 4 possible mutations on the update user modal:
 * 1. Update a users first name or last name
 * 2. Update a users role
 * 3. Activate a user
 * 4. Deactivate a user
 */
const getFormMutationInputs = (
    initialForm: UpdateUserFormI,
    newForm: UpdateUserFormI,
    subjectID: string
): UpdateUserModalMutationInputs => {
    const usersUpdateAdminInput = getUsersUpdateAdminInput(
        initialForm,
        newForm,
        subjectID
    )
    const usersUpdateOrgMemInput = getUsersUpdateOrgMemInput(
        initialForm,
        newForm,
        subjectID
    )
    const activationInputs = getUsersActivationInputs(
        initialForm,
        newForm,
        subjectID
    )

    return {
        usersUpdateAdminInput: usersUpdateAdminInput,
        usersUpdateOrgMemInput: usersUpdateOrgMemInput,
        usersActivateInput: activationInputs.activateInput,
        usersDeactivateInput: activationInputs.deactivateInput,
    }
}

export { isSubmitFormDisabled, getFormMutationInputs, sanitizeForm }

/**
 * Helper function to get the activation and deactivation inputs for the usersActivateInput and usersDeactivateInput fields.
 * Used to update a users activation status
 * If the initial and updated status are the same, we return undefined for both fields.
 * If the updated status is true and the initial status is false, we return the activateInput field.
 * If the updated status is false and the initial status is true, we return the deactivateInput field.
 */
const getUsersActivationInputs = (
    initialForm: UpdateUserFormI,
    updatedForm: UpdateUserFormI,
    userId: string
): {
    activateInput: UsersActivateInput | undefined
    deactivateInput: UsersDeactivateInput | undefined
} => {
    const intialStatus = initialForm.activeUser
    const updatedStatus = updatedForm.activeUser

    if (intialStatus === updatedStatus) {
        return {
            activateInput: undefined,
            deactivateInput: undefined,
        }
    }

    if (updatedStatus) {
        const activateInput: UsersActivateInput = {
            userIDs: [userId],
        }
        return {
            activateInput: activateInput,
            deactivateInput: undefined,
        }
    } else {
        const deactivateInput: UsersDeactivateInput = {
            userIDs: [userId],
        }
        return {
            activateInput: undefined,
            deactivateInput: deactivateInput,
        }
    }
}

/**
 * Helper function to get the organization membership input for the usersUpdateOrgMemInput field.
 * Used to update a users role
 */
const getUsersUpdateOrgMemInput = (
    initialForm: UpdateUserFormI,
    updatedForm: UpdateUserFormI,
    subjectID: string
): UsersUpdateOrganizationMembershipInput | undefined => {
    const roleUpdated = initialForm.userRole !== updatedForm.userRole

    if (roleUpdated) {
        const userOrgMembershipVariablesInput: UsersUpdateOrganizationMembershipInput =
            {
                users: [
                    {
                        userID: subjectID,
                        role: updatedForm.userRole,
                    },
                ],
            }

        return userOrgMembershipVariablesInput
    }

    return undefined
}

/**
 * Helper function to get the admin input for the usersUpdateAdminInput field.
 * Used to update a users first name or last name
 */
const getUsersUpdateAdminInput = (
    initialForm: UpdateUserFormI,
    updatedForm: UpdateUserFormI,
    subjectID: string
): UsersUpdateAdminInput | undefined => {
    const firstNameUpdated = initialForm.firstName !== updatedForm.firstName
    const lastNameUpdated = initialForm.lastName !== updatedForm.lastName

    if (firstNameUpdated || lastNameUpdated) {
        const uuInput: UsersUpdateAdminInput = {
            users: [
                {
                    userID: subjectID,
                },
            ],
        }

        if (firstNameUpdated) {
            uuInput.users[0].firstName = updatedForm.firstName
        }

        if (lastNameUpdated) {
            uuInput.users[0].lastName = updatedForm.lastName
        }

        const userVariablesInput: UsersUpdateAdminInput = uuInput

        return userVariablesInput
    }

    return undefined
}
